import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useUserContext } from '../../contexts/UserContext';
import { getPermissions } from '../../utils/APIs';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { Input, AgTableCard } from '../UI';

const Permissions = () => {
    const navigate = useNavigate();
    const { content } = useContentfulContext();

    const [gridApi, setGridApi] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState([]);
    const { userData } = useUserContext();

    const getAllPermissions = async () => {
        try {
            setLoading(true);
            const res = await getPermissions(userData?.token, userData?.selectedOrganization?.org_token);
            setRowData(res.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllPermissions();
    }, []);

    const autoSizeStrategy = useMemo(() => ({
        type: "fitGridWidth",
        defaultMinWidth: 120,
        columnLimits: [
            { colId: "role_id", minWidth: 200 },
            { colId: "level", minWidth: 160 },
            { colId: "configuration", minWidth: 180 },
        ],
    }), []);

    const colDefs = [
        {
            headerName: content.PERMISSION.table_headers.role_id,
            field: "role_id",
            cellRenderer: ({ data }) => (
                <span className="font-mono text-xs font-semibold text-stride-heading">
                    {data?.role_id}
                </span>
            ),
            sortable: true,
            filter: true,
            suppressMovable: true
        },
        {
            headerName: content.PERMISSION.table_headers.level,
            field: "level",
            cellRenderer: ({ value }) => (
                <span className="stride-badge-pill bg-purple-50 text-purple-700 border border-purple-200 text-xs font-semibold">
                    Level {value ? value : "N/A"}
                </span>
            ),
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: content.PERMISSION.table_headers.permission,
            field: "configuration",
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className="font-medium text-xs text-primary hover:text-primary-hover hover:underline cursor-pointer"
                    onClick={() => {
                        navigate(`/admin/permissions/${data._id}`, { state: { data } });
                    }}
                >
                    View details
                </button>
            ),
            suppressMovable: true
        }
    ];

    const onFilterTextBoxChanged = (event) => {
        const filterValue = (event.target.value || '').toLowerCase();
        gridApi.setFilterModel({
            role_id: { type: 'contains', filter: filterValue }
        });
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    return (
        <main id="main" className="stride-page-shell flex flex-col space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <span className="stride-eyebrow">Administration</span>
                    <div className="flex items-baseline gap-3">
                        <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                            {content.PERMISSION.title}
                        </h1>
                        <span className="text-xs text-stride-muted font-normal">
                            {rowData.length} {rowData.length === 1 ? 'role' : 'roles'}
                        </span>
                    </div>
                </div>
            </div>

            <AgTableCard
                header={
                    <div className="w-full sm:w-80">
                        <Input
                            placeholder={content.PERMISSION.search_placeholder}
                            onChange={onFilterTextBoxChanged}
                            prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                            clearable
                        />
                    </div>
                }
                tableId="permissions-table"
                loading={loading}
                rowData={rowData}
                columnDefs={colDefs}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={onGridReady}
            />
        </main>
    );
};

export default Permissions;
